import { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import {
  Video,
  Upload,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  Play,
  Settings,
  ScanBarcode,
  FileVideo
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import BatchConfirmationGrid from './BatchConfirmationGrid';

// Max file size: 100MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024;

// Valid video extensions
const VALID_VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.webm', '.mkv'];

/**
 * VideoScanUpload - Phase 2C Component
 * 
 * Allows DRLPs to upload shelf walkthrough videos for barcode extraction.
 * Videos are processed server-side with configurable frame sampling.
 */
export default function VideoScanUpload({ onComplete, onCancel }) {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  
  // State
  const [mode, setMode] = useState('upload'); // 'upload', 'preview', 'processing', 'results'
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Settings
  const [showSettings, setShowSettings] = useState(false);
  const [frameInterval, setFrameInterval] = useState(0.5);
  const [maxFrames, setMaxFrames] = useState(60);

  // Handle file selection
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const filename = file.name.toLowerCase();
    if (!VALID_VIDEO_EXTENSIONS.some(ext => filename.endsWith(ext))) {
      setError('Please select a video file (MP4, MOV, AVI, WebM)');
      return;
    }
    
    // Validate file size
    if (file.size > MAX_VIDEO_SIZE) {
      setError('Video too large. Maximum size is 100MB.');
      return;
    }
    
    setError(null);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMode('preview');
  }, []);

  // Handle drag and drop
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // Reset state
  const handleReset = useCallback(() => {
    setMode('upload');
    setSelectedFile(null);
    setPreviewUrl(null);
    setScanResults(null);
    setError(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Process video
  const handleProcess = useCallback(async () => {
    if (!selectedFile) return;
    
    setProcessing(true);
    setMode('processing');
    setError(null);
    setProgress(5);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      // Simulate progress during upload
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 5, 50));
      }, 500);
      
      const response = await api.post(
        `/barcode/process-video?frame_interval=${frameInterval}&max_frames=${maxFrames}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // 2 minute timeout for video processing
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 30) / progressEvent.total);
            setProgress(percentCompleted);
          }
        }
      );
      
      clearInterval(progressInterval);
      setProgress(90);
      
      if (response.data.success) {
        setScanResults(response.data);
        setMode('results');
        
        const { count, ready_count, metadata } = response.data;
        toast.success(
          `Found ${count} products from ${metadata?.frames_sampled || 0} frames`
        );
      } else {
        setError(response.data.message || 'No barcodes detected in video');
        setMode('preview');
      }
      
      setProgress(100);
      
    } catch (err) {
      console.error('Video processing error:', err);
      const message = err.response?.data?.detail || 
                      (err.code === 'ECONNABORTED' ? 'Processing timed out. Try a shorter video.' : 
                       'Failed to process video. Please try again.');
      setError(message);
      setMode('preview');
    } finally {
      setProcessing(false);
    }
  }, [selectedFile, frameInterval, maxFrames]);

  // Submit batch
  const handleBatchSubmit = useCallback(async (items) => {
    if (!items || items.length === 0) return;
    
    setSubmitting(true);
    
    try {
      const response = await api.post('/barcode/batch-create-rshd', { items });
      
      if (response.data.success) {
        const { created, failed } = response.data.summary;
        
        if (created > 0) {
          toast.success(`Successfully posted ${created} products!`);
        }
        if (failed > 0) {
          toast.warning(`${failed} products could not be posted`);
        }
        
        if (onComplete) {
          onComplete(response.data);
        }
        
        handleReset();
      }
    } catch (err) {
      console.error('Batch submit error:', err);
      toast.error(err.response?.data?.detail || 'Failed to post products');
    } finally {
      setSubmitting(false);
    }
  }, [onComplete, handleReset]);

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Render upload zone
  const renderUploadZone = () => (
    <Card className="border-2 border-dashed border-gray-300 hover:border-purple-400 transition-colors">
      <CardContent className="pt-6">
        <div
          className="flex flex-col items-center justify-center py-12 cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          data-testid="video-upload-zone"
        >
          <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4">
            <Video className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('video_scan.upload_title', 'Upload Shelf Video')}
          </h3>
          <p className="text-sm text-gray-500 text-center max-w-sm mb-4">
            {t('video_scan.upload_desc', 'Record a walkthrough of your shelf showing barcodes. We\'ll extract all products automatically.')}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <FileVideo className="w-4 h-4" />
            <span>MP4, MOV, AVI, WebM • Max 100MB</span>
          </div>
          
          <Button className="mt-6 bg-purple-600 hover:bg-purple-700" data-testid="select-video-btn">
            <Upload className="w-4 h-4 mr-2" />
            {t('video_scan.select_video', 'Select Video')}
          </Button>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="video-file-input"
        />
      </CardContent>
    </Card>
  );

  // Render preview
  const renderPreview = () => (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-600" />
            {t('video_scan.preview', 'Video Preview')}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSettings(!showSettings)}
              title="Processing settings"
            >
              <Settings className={`w-4 h-4 ${showSettings ? 'text-purple-600' : 'text-gray-400'}`} />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleReset}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <CardDescription>
          {selectedFile?.name} • {formatSize(selectedFile?.size || 0)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Video Preview */}
        <div className="relative rounded-lg overflow-hidden bg-gray-900 mb-4">
          <video
            src={previewUrl}
            className="w-full max-h-[300px] object-contain"
            controls
            data-testid="preview-video"
          />
        </div>
        
        {/* Settings Panel */}
        {showSettings && (
          <div className="p-4 bg-gray-50 rounded-lg mb-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Processing Settings</h4>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label className="text-xs">Frame Interval: {frameInterval}s</Label>
              </div>
              <Slider
                value={[frameInterval]}
                onValueChange={([v]) => setFrameInterval(v)}
                min={0.2}
                max={2}
                step={0.1}
                className="w-full"
              />
              <p className="text-xs text-gray-400">
                Lower = more frames analyzed (slower but more thorough)
              </p>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label className="text-xs">Max Frames: {maxFrames}</Label>
              </div>
              <Slider
                value={[maxFrames]}
                onValueChange={([v]) => setMaxFrames(v)}
                min={20}
                max={150}
                step={10}
                className="w-full"
              />
              <p className="text-xs text-gray-400">
                More frames = longer processing time
              </p>
            </div>
          </div>
        )}
        
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleReset} className="flex-1">
            {t('video_scan.choose_different', 'Choose Different')}
          </Button>
          <Button
            onClick={handleProcess}
            disabled={processing}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
            data-testid="process-video-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            {t('video_scan.process', 'Process Video')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Render processing
  const renderProcessing = () => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('video_scan.processing', 'Processing Video...')}
          </h3>
          <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
            {t('video_scan.processing_desc', 'Extracting frames and detecting barcodes. This may take a minute.')}
          </p>
          
          <div className="w-full max-w-xs">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-gray-400 text-center mt-2">{progress}%</p>
          </div>
          
          <p className="text-xs text-gray-400 mt-4">
            Sampling every {frameInterval}s • Max {maxFrames} frames
          </p>
        </div>
      </CardContent>
    </Card>
  );

  // Render results
  const renderResults = () => {
    if (!scanResults || !scanResults.barcodes) return null;
    
    const { metadata } = scanResults;
    
    return (
      <div className="space-y-4">
        {/* Summary Header */}
        <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <CheckCircle2 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {t('video_scan.complete', 'Video Processing Complete')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {scanResults.count} products from {metadata?.frames_sampled || 0} frames
                    ({metadata?.video_duration_seconds?.toFixed(1) || 0}s video)
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Badge variant="outline" className="bg-purple-50 text-purple-700">
                  {scanResults.ready_count} ready
                </Badge>
                {scanResults.needs_input_count > 0 && (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                    {scanResults.needs_input_count} need input
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Batch Confirmation Grid */}
        <BatchConfirmationGrid
          items={scanResults.barcodes}
          onSubmit={handleBatchSubmit}
          onCancel={handleReset}
          loading={submitting}
          title={t('video_scan.review', 'Review & Post Products')}
          submitLabel={t('video_scan.post_selected', 'Post Selected')}
        />
        
        {/* Back to upload */}
        <div className="text-center">
          <Button variant="ghost" onClick={handleReset}>
            <Video className="w-4 h-4 mr-2" />
            {t('video_scan.upload_another', 'Process Another Video')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="video-scan-upload">
      {mode === 'upload' && renderUploadZone()}
      {mode === 'preview' && renderPreview()}
      {mode === 'processing' && renderProcessing()}
      {mode === 'results' && renderResults()}
    </div>
  );
}
